import axios from "axios";
import {
  dynAttr,
  getCurrentUserRole,
  getOrgId,
  getStoredOrganization,
  isAdminOrOwner,
} from "./orgHelpers";
import { resolveNavigatePath } from "./navigation";
import {
  TWILANSWER_APP_ACTION,
  isDeviceSentinel,
  listOrgUsersForDeviceForward,
  parseDeviceUserId,
  toDeviceSentinel,
} from "../utils/forwardDeviceDestinations";

function adminDeniedMessage(actionLabel) {
  const role = getCurrentUserRole() || "user";
  return (
    `This account is a member (${role}), not an admin/owner. ` +
    `Only admins can ${actionLabel}. Suggest they ask an org admin, ` +
    `or continue with setup steps they can do themselves.`
  );
}

function requireAdmin(actionLabel) {
  if (!isAdminOrOwner()) {
    const err = new Error(adminDeniedMessage(actionLabel));
    err.isPermissionError = true;
    throw err;
  }
}

function summarizeChannel(ch) {
  const m = ch?.M || ch || {};
  const chId = dynAttr(m.ch_id);
  return {
    id: chId || dynAttr(m.id) || dynAttr(m.channel_id) || dynAttr(m.value) || null,
    ch_id: chId || null,
    type: dynAttr(m.type),
    label: dynAttr(m.label) || dynAttr(m.name) || null,
    value: dynAttr(m.value),
    created:
      dynAttr(m.created) ||
      dynAttr(m.created_at) ||
      dynAttr(m.last_updated) ||
      null,
    status: dynAttr(m.status),
  };
}

const NOTIFICATION_TYPES = new Set(["email", "slack", "sms", "front"]);

function resolveChannelId(channelRef) {
  if (!channelRef) return null;
  const channels = getStoredOrganization()?.channels?.L || [];
  const match = channels.find((c) => {
    const m = c?.M || c || {};
    return (
      channelRef === dynAttr(m.ch_id) ||
      channelRef === dynAttr(m.value) ||
      channelRef === dynAttr(m.id) ||
      channelRef === dynAttr(m.channel_id)
    );
  });
  if (!match) return channelRef;
  return dynAttr(match.M?.ch_id ?? match.ch_id) || channelRef;
}

function normalizeNotificationType(raw) {
  if (raw == null || raw === "") return null;
  const type = String(raw).toLowerCase();
  return NOTIFICATION_TYPES.has(type) ? type : null;
}

function isExplicitClear(value) {
  return value === null || value === "";
}

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj || {}, key);
}

/** Parse DynamoDB AttributeValue-shaped objects into plain JS. */
function parseDynamoItem(item) {
  if (!item || typeof item !== "object") return item;
  if (item.S !== undefined) return item.S;
  if (item.N !== undefined) return Number(item.N);
  if (item.BOOL !== undefined) return item.BOOL;
  if (item.NULL) return null;
  if (item.L) return item.L.map(parseDynamoItem);
  if (item.M) {
    const result = {};
    for (const [key, value] of Object.entries(item.M)) {
      result[key] = parseDynamoItem(value);
    }
    return result;
  }
  // Plain JS arrays must stay arrays (Object.entries would yield { "0": … }).
  if (Array.isArray(item)) {
    return item.map(parseDynamoItem);
  }
  const result = {};
  for (const [key, value] of Object.entries(item)) {
    result[key] = parseDynamoItem(value);
  }
  return result;
}

function routeIdFromPath(pathname) {
  const m = String(pathname || "").match(/\/call-route\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

function editPathForRouteId(routeId) {
  if (!routeId) return null;
  return `/dashboard/call-route/${routeId}`;
}

/** Compact list row for the agent. */
function summarizeRouteListItem(raw) {
  const r = parseDynamoItem(raw) || {};
  const routeId = r.routeId || r.route_id || null;
  const primaryAction = r.primaryAction ?? r.primary_action ?? null;
  const offHoursAction = r.offHoursAction ?? r.offhours_action ?? null;
  const businessHoursEnabled =
    r.businessHoursEnabled === true ||
    r.businessHoursEnabled === false
      ? r.businessHoursEnabled
      : Object.values(r.calendar || {}).some((d) => d?.active === true);
  return {
    routeId,
    name: r.name || null,
    configured_phone: r.configured_phone || null,
    primaryAction,
    offHoursAction,
    businessHoursEnabled: Boolean(businessHoursEnabled),
    editPath: editPathForRouteId(routeId),
  };
}

/** Agent-facing route config (matches updateCurrentRouteConfig field names). */
function summarizeRouteConfig(raw, { source = "api" } = {}) {
  const r = raw && typeof raw === "object" ? { ...raw } : {};
  if (r.primary_action != null && r.primaryAction == null) {
    r.primaryAction = r.primary_action;
  }
  if (r.offhours_action != null && r.offHoursAction == null) {
    r.offHoursAction = r.offhours_action;
  }
  const routeId = r.routeId || r.route_id || null;
  const businessHoursEnabled =
    r.businessHoursEnabled === true || r.businessHoursEnabled === false
      ? r.businessHoursEnabled
      : Object.values(r.calendar || {}).some((d) => d?.active === true);
  return {
    routeId,
    name: r.name || null,
    configured_phone: r.configured_phone || null,
    businessHoursEnabled:
      r.businessHoursEnabled === true || r.businessHoursEnabled === false
        ? r.businessHoursEnabled
        : Boolean(businessHoursEnabled),
    calendar: r.calendar || null,
    primaryAction: r.primaryAction || null,
    primaryNumber: r.primaryNumber || null,
    primaryRecording: r.primaryRecording || null,
    primaryNotification: r.primaryNotification || null,
    primaryChannel: r.primaryChannel || null,
    callerIdPref: r.callerIdPref ?? null,
    callApproval: r.callApproval ?? null,
    twilanswerVmFallback: r.twilanswerVmFallback ?? null,
    offHoursTwilanswerVmFallback: r.offHoursTwilanswerVmFallback ?? null,
    callGreetingEnabled: r.callGreetingEnabled ?? null,
    callGreetingRecording: r.callGreetingRecording || null,
    offHoursAction: r.offHoursAction || null,
    offHoursNumber: r.offHoursNumber || null,
    offHoursNotification: r.offHoursNotification || null,
    offHoursChannel: r.offHoursChannel || null,
    offHoursRecording: r.offHoursRecording || null,
    ivrMenu: r.ivrMenu || null,
    recordCall: r.recordCall ?? null,
    transcribeCall: r.transcribeCall ?? null,
    editPath: editPathForRouteId(routeId),
    source,
  };
}

/**
 * Strict mapping only — no shorthand / coercion.
 * Explicit null/"" clears. Omitted keys are left unchanged.
 */
function buildNotificationPatch(params = {}) {
  const patch = {};

  if (hasOwn(params, "primaryNotification")) {
    patch.primaryNotification = isExplicitClear(params.primaryNotification)
      ? null
      : normalizeNotificationType(params.primaryNotification);
  }
  if (hasOwn(params, "offHoursNotification")) {
    patch.offHoursNotification = isExplicitClear(params.offHoursNotification)
      ? null
      : normalizeNotificationType(params.offHoursNotification);
  }
  if (hasOwn(params, "primaryChannel")) {
    patch.primaryChannel = isExplicitClear(params.primaryChannel)
      ? null
      : resolveChannelId(params.primaryChannel);
  }
  if (hasOwn(params, "offHoursChannel")) {
    patch.offHoursChannel = isExplicitClear(params.offHoursChannel)
      ? null
      : resolveChannelId(params.offHoursChannel);
  }

  return patch;
}

function applyClearFields(patch, clearFields) {
  if (!Array.isArray(clearFields)) return patch;
  for (const key of clearFields) {
    if (typeof key === "string" && key.trim()) {
      patch[key.trim()] = null;
    }
  }
  return patch;
}

function digitsOnly(value) {
  return String(value || "").replace(/\D/g, "");
}

function listOrgChannels() {
  return (getStoredOrganization()?.channels?.L || [])
    .map(summarizeChannel)
    .filter((c) => c.id || c.value);
}

function listOrgForwardingNumbers() {
  return listOrgChannels().filter((c) => c.type === "forward_number" && c.value);
}

function listOrgRecordings() {
  return (getStoredOrganization()?.recordings?.L || [])
    .map(summarizeRecording)
    .filter((r) => r.id);
}

/**
 * Resolve a forwarding destination to the org's canonical E.164 value.
 * Returns null when the number/id is not a forward_number channel on this org.
 */
function findForwardingNumberValue(phoneOrId) {
  if (phoneOrId == null || phoneOrId === "") return null;
  const forwards = listOrgForwardingNumbers();

  const byId = forwards.find((c) => phoneOrId === c.id || phoneOrId === c.ch_id);
  if (byId?.value) return byId.value;

  const byExact = forwards.find((c) => phoneOrId === c.value);
  if (byExact?.value) return byExact.value;

  const byDigits = forwards.find((c) => phonesMatch(phoneOrId, c.value));
  return byDigits?.value || null;
}

/** Match forwarding channel value (exact, ch_id, or digit variants). */
function resolveForwardingPhone(phoneOrId) {
  if (!phoneOrId) return phoneOrId;
  return findForwardingNumberValue(phoneOrId) || phoneOrId;
}

function findOrgChannel(channelRef) {
  if (channelRef == null || channelRef === "") return null;
  const channels = listOrgChannels();
  return (
    channels.find(
      (c) =>
        channelRef === c.id ||
        channelRef === c.ch_id ||
        channelRef === c.value
    ) || null
  );
}

function findOrgRecording(recordingId) {
  if (recordingId == null || recordingId === "" || recordingId === "disabled") {
    return null;
  }
  const id = String(recordingId);
  return listOrgRecordings().find((r) => r.id === id) || null;
}

function formatForwardingHint() {
  const forwards = listOrgForwardingNumbers();
  if (!forwards.length) {
    return (
      "This org has no forwarding numbers yet. Call appClient_promptAddForwardingNumber " +
      "(do not ask for the number in chat), wait for channel_added, then retry with that number."
    );
  }
  const listed = forwards
    .slice(0, 12)
    .map((f) => `${f.value}${f.label ? ` (${f.label})` : ""}`)
    .join(", ");
  return (
    `Available forwarding numbers: ${listed}. ` +
    "Call appClient_fetchOrgForwardingNumbers and use one of those values, " +
    "or appClient_promptAddForwardingNumber to add a new one first."
  );
}

function listOrgMobileDestinations() {
  const users = listOrgUsersForDeviceForward(getStoredOrganization());
  return users.map((u) => ({
    value: toDeviceSentinel(u.userId),
    userId: u.userId,
    name: u.name,
    hasForwardingDevice: u.hasForwardingDevices,
    devices: u.devices.map((d) => ({
      label: d.label,
      platform: d.platform,
    })),
  }));
}

function findOrgUserId(userId) {
  if (!userId) return null;
  const users = listOrgUsersForDeviceForward(getStoredOrganization());
  return users.find((u) => u.userId === userId)?.userId || null;
}

function formatMobileDestinationHint() {
  const mobiles = listOrgMobileDestinations();
  if (!mobiles.length) {
    return "This org has no users yet. Invite a user, then retry with device:<userId>.";
  }
  const listed = mobiles
    .slice(0, 12)
    .map(
      (m) =>
        `${m.value} (${m.name}${m.hasForwardingDevice ? "" : "; no app registered"})`
    )
    .join(", ");
  return (
    `Available Twilanswer App destinations: ${listed}. ` +
    "Prefer users with hasForwardingDevice true. Call appClient_fetchOrgForwardingNumbers for the full list."
  );
}

function formatRecordingHint() {
  const recordings = listOrgRecordings();
  if (!recordings.length) {
    return (
      "This org has no recordings yet. Call appClient_promptAddRecording, wait for recording_added, then retry."
    );
  }
  const listed = recordings
    .slice(0, 15)
    .map((r) => `${r.id} (“${r.name}”)`)
    .join(", ");
  return `Available recordings: ${listed}. Call appClient_fetchOrgRecordings and use an id from that list.`;
}

function formatChannelHint(expectedType) {
  const channels = listOrgChannels().filter((c) =>
    expectedType ? c.type === expectedType : NOTIFICATION_TYPES.has(c.type)
  );
  if (!channels.length) {
    return expectedType
      ? `No ${expectedType} channels on this org. Call appClient_promptAddChannel with type "${expectedType}" first.`
      : "No notification channels on this org. Call appClient_promptAddChannel first.";
  }
  const listed = channels
    .slice(0, 12)
    .map((c) => `${c.id} (${c.type}${c.label ? `: ${c.label}` : ""})`)
    .join(", ");
  return `Available channels: ${listed}. Call appClient_fetchOrgChannels and use an id from that list.`;
}

/**
 * Reject references that are not on the signed-in org (phones, channels, recordings).
 * Must run after the patch is assembled (including IVR menu).
 */
function validateOrgEntityRefs(patch = {}) {
  const errors = [];

  const checkDevice = (field, value) => {
    if (value == null || value === "") return;
    if (!isDeviceSentinel(value)) {
      errors.push(
        `${field} for twilanswer_app must be device:<userId> (e.g. "device:user_abc"), not a phone number. ${formatMobileDestinationHint()}`
      );
      return;
    }
    const userId = parseDeviceUserId(value);
    if (!userId) {
      errors.push(
        `${field} is an incomplete device sentinel. Set device:<userId> for a user on this org. ${formatMobileDestinationHint()}`
      );
      return;
    }
    if (!findOrgUserId(userId)) {
      errors.push(
        `${field} "${value}" does not match a user on this organization. ${formatMobileDestinationHint()}`
      );
    }
  };

  const checkForward = (field, value, { allowNone = false, action = null } = {}) => {
    if (value == null || value === "") return;
    if (action === TWILANSWER_APP_ACTION || isDeviceSentinel(value)) {
      if (action === TWILANSWER_APP_ACTION || action == null) {
        // Device destinations are validated via checkDevice when action is twilanswer_app.
        // If a non-app action somehow has a device sentinel, reject it here.
        if (action !== TWILANSWER_APP_ACTION && isDeviceSentinel(value)) {
          errors.push(
            `${field} "${value}" is a Twilanswer App destination. Use primaryAction "twilanswer_app" for device:<userId>, or pick an org forwarding number.`
          );
        }
        return;
      }
    }
    if (allowNone && value === "none") return;
    if (!findForwardingNumberValue(value)) {
      errors.push(
        `${field} "${value}" is not a forwarding number on this organization. ${formatForwardingHint()}`
      );
    }
  };

  const checkRecording = (field, value) => {
    if (value == null || value === "" || value === "disabled") return;
    if (!findOrgRecording(value)) {
      errors.push(
        `${field} "${value}" is not a recording on this organization. ${formatRecordingHint()}`
      );
    }
  };

  const checkNotifyChannel = (channelField, channelValue, typeField, typeValue) => {
    if (channelValue == null || channelValue === "") return;
    const channel = findOrgChannel(channelValue);
    if (!channel) {
      errors.push(
        `${channelField} "${channelValue}" is not a channel on this organization. ${formatChannelHint(typeValue || null)}`
      );
      return;
    }
    if (channel.type === "forward_number") {
      errors.push(
        `${channelField} "${channelValue}" is a forwarding number, not a notification channel. ` +
          `Use ${typeField === "offHoursNotification" ? "offHoursNumber" : "primaryNumber"} for phones. ` +
          formatChannelHint(typeValue || null)
      );
      return;
    }
    if (typeValue && channel.type && channel.type !== typeValue) {
      errors.push(
        `${channelField} "${channelValue}" is type "${channel.type}" but ${typeField} is "${typeValue}". ` +
          `Pick a ${typeValue} channel. ${formatChannelHint(typeValue)}`
      );
    }
  };

  if (patch.primaryAction === TWILANSWER_APP_ACTION) {
    checkDevice("primaryNumber", patch.primaryNumber);
  } else {
    checkForward("primaryNumber", patch.primaryNumber, {
      allowNone: patch.primaryAction === "dynamic_forward",
      action: patch.primaryAction,
    });
  }
  if (patch.offHoursAction === TWILANSWER_APP_ACTION) {
    checkDevice("offHoursNumber", patch.offHoursNumber);
  } else {
    checkForward("offHoursNumber", patch.offHoursNumber, {
      allowNone: patch.offHoursAction === "dynamic_forward",
      action: patch.offHoursAction,
    });
  }

  checkRecording("primaryRecording", patch.primaryRecording);
  checkRecording("offHoursRecording", patch.offHoursRecording);
  checkRecording("callGreetingRecording", patch.callGreetingRecording);

  checkNotifyChannel(
    "primaryChannel",
    patch.primaryChannel,
    "primaryNotification",
    patch.primaryNotification
  );
  checkNotifyChannel(
    "offHoursChannel",
    patch.offHoursChannel,
    "offHoursNotification",
    patch.offHoursNotification
  );

  const menu = patch.ivrMenu;
  if (menu && typeof menu === "object") {
    checkRecording("ivrMenu.menu_recording", menu.menu_recording);
    checkRecording("ivrMenu.hangup_recording", menu.hangup_recording);
    const opts = menu.options
      ? Array.isArray(menu.options)
        ? menu.options
        : Object.values(menu.options)
      : [];
    opts.forEach((o, i) => {
      const digit = o?.digit ?? i + 1;
      if (o?.primaryAction === TWILANSWER_APP_ACTION) {
        checkDevice(`ivrMenu option ${digit} primaryNumber`, o?.primaryNumber);
      } else {
        checkForward(`ivrMenu option ${digit} primaryNumber`, o?.primaryNumber, {
          allowNone: o?.primaryAction === "dynamic_forward",
          action: o?.primaryAction,
        });
      }
      checkRecording(
        `ivrMenu option ${digit} primaryRecording`,
        o?.primaryRecording
      );
      if (o?.primaryChannel) {
        checkNotifyChannel(
          `ivrMenu option ${digit} primaryChannel`,
          o.primaryChannel,
          "primaryNotification",
          o.primaryNotification || null
        );
      }
    });
  }

  return errors;
}

const IVR_OPTION_ACTIONS = new Set([
  "forward",
  "forwardvwm",
  "dynamic_forward",
  "twilanswer_app",
  "send_to_vm",
  "play_recording",
  "ring_browser",
  "ai_agent",
]);

/** Parse JSON strings ElevenLabs sometimes delivers for nested tool params. */
function coerceJsonValue(value) {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== "{" && trimmed[0] !== "[")) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function optionEntryCount(options) {
  if (!options) return 0;
  if (Array.isArray(options)) return options.length;
  if (typeof options === "object") return Object.keys(options).length;
  return 0;
}

function normalizeOneIvrOption(raw, fallbackDigit) {
  const parsed = coerceJsonValue(raw);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  const digit = String(parsed.digit ?? parsed.key ?? fallbackDigit);
  const action =
    parsed.primaryAction ||
    parsed.action ||
    (parsed.number || parsed.primaryNumber ? "forward" : null);
  const rawNumber = parsed.primaryNumber || parsed.number || parsed.to || null;
  const number =
    rawNumber === "none"
      ? "none"
      : resolveForwardingPhone(rawNumber);
  const recording =
    parsed.primaryRecording || parsed.recording || parsed.recordingId || null;
  const isDynamic = action === "dynamic_forward";
  const twilanswerVmFallback =
    parsed.twilanswerVmFallback ??
    parsed.twilanswer_vm_fallback ??
    (isDynamic ? true : null);
  const callerIdPref = parsed.callerIdPref ?? (isDynamic ? "twilio" : null);
  const callApproval = parsed.callApproval ?? (isDynamic ? "enabled" : null);
  return {
    digit,
    id: parsed.id || `press_${digit}`,
    name: parsed.name || `Press ${digit}`,
    primaryAction: action,
    primaryNumber: number,
    primaryRecording: recording,
    notification: parsed.notification ?? null,
    ...(twilanswerVmFallback != null
      ? { twilanswerVmFallback: Boolean(twilanswerVmFallback) }
      : {}),
    ...(callerIdPref != null ? { callerIdPref } : {}),
    ...(callApproval != null ? { callApproval } : {}),
    ...(parsed.primaryChannel ? { primaryChannel: parsed.primaryChannel } : {}),
    ...(parsed.primaryNotification
      ? { primaryNotification: parsed.primaryNotification }
      : {}),
  };
}

/**
 * Build ivrMenu from explicit ivrMenu and/or convenience aliases the model may send
 * (ivrOptions array, ivrMenuRecording, etc.).
 * Returns { ivrMenu } or null if nothing IVR-related was provided.
 */
function buildIvrMenuFromParams(params = {}, extraFields = {}) {
  const src = { ...(extraFields || {}), ...(params || {}) };
  const hasIvrHint =
    src.ivrMenu != null ||
    src.ivrMenuRecording != null ||
    src.ivrHangupRecording != null ||
    src.ivrNoResponseRecording != null ||
    src.menu_recording != null ||
    src.hangup_recording != null ||
    src.ivrOptions != null ||
    params.primaryAction === "ivr";

  if (!hasIvrHint && params.primaryAction !== "ivr") return null;

  let existing = coerceJsonValue(src.ivrMenu);
  existing =
    existing && typeof existing === "object" && !Array.isArray(existing)
      ? { ...existing }
      : {};

  const menuRecording =
    src.ivrMenuRecording ??
    src.menu_recording ??
    existing.menu_recording ??
    null;
  const hangupRecording =
    src.ivrHangupRecording ??
    src.ivrNoResponseRecording ??
    src.hangup_recording ??
    existing.hangup_recording ??
    null;

  let optionsSource = coerceJsonValue(
    src.ivrOptions ?? existing.options ?? null
  );
  const options = {};

  if (Array.isArray(optionsSource)) {
    optionsSource.forEach((raw, index) => {
      const opt = normalizeOneIvrOption(raw, String(index + 1));
      if (opt) options[opt.digit] = opt;
    });
  } else if (optionsSource && typeof optionsSource === "object") {
    Object.entries(optionsSource).forEach(([key, raw], index) => {
      const opt = normalizeOneIvrOption(raw, String(key || index + 1));
      if (opt) options[opt.digit] = opt;
    });
  }

  const ivrMenu = {
    ...existing,
    ...(menuRecording != null ? { menu_recording: menuRecording } : {}),
    ...(hangupRecording != null ? { hangup_recording: hangupRecording } : {}),
  };

  if (Object.keys(options).length) {
    ivrMenu.options = options;
  } else if (optionEntryCount(existing.options) > 0) {
    // Keep only non-empty existing options (empty {} often means schema stripped keys)
    ivrMenu.options = existing.options;
  } else {
    delete ivrMenu.options;
  }

  // Nothing useful beyond an empty shell
  if (
    !ivrMenu.menu_recording &&
    !ivrMenu.hangup_recording &&
    optionEntryCount(ivrMenu.options) === 0
  ) {
    return null;
  }

  return ivrMenu;
}

function summarizeIvrMenu(ivrMenu) {
  if (!ivrMenu || typeof ivrMenu !== "object") return null;
  const opts = ivrMenu.options || {};
  const optionEntries = Array.isArray(opts)
    ? opts.map((o, i) => [String(o?.digit ?? i + 1), o])
    : Object.entries(opts);
  return {
    menu_recording: ivrMenu.menu_recording || null,
    hangup_recording: ivrMenu.hangup_recording || null,
    options: optionEntries.map(([digit, o]) => ({
      digit: String(o?.digit ?? digit),
      primaryAction: o?.primaryAction || null,
      primaryNumber: o?.primaryNumber || null,
      primaryRecording: o?.primaryRecording || null,
    })),
  };
}

function hasActiveCalendarDays(calendar) {
  if (!calendar || typeof calendar !== "object") return false;
  return Object.values(calendar).some((day) => day?.active === true);
}

/** Coerce business_hours / calendar from object or JSON string. Empty objects → null. */
function coerceBusinessHoursCalendar(value) {
  if (value == null || value === "") return null;
  let parsed = value;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  if (Object.keys(parsed).length === 0) return null;
  return parsed;
}

/** Return a corrective error string if the agent used the wrong fields. */
function validateRouteDraftParams(params = {}, patch = {}) {
  const errors = [];

  if (params.notification != null) {
    errors.push(
      "Do not use `notification`. Set top-level `primaryNotification` + `primaryChannel` " +
        "(and `offHoursNotification` + `offHoursChannel` when needed)."
    );
  }

  if (patch.businessHoursEnabled === true && !hasActiveCalendarDays(patch.calendar)) {
    errors.push(
      "businessHoursEnabled is true but business_hours has no active days " +
        "(received empty/missing calendar). Pass business_hours with day keys " +
        "mon,tue,wed,thur,fri,sat,sun each as {active, earlyTime, lateTime}. " +
        'If the object keeps arriving empty, pass the same schedule as business_hours_json ' +
        '(a JSON string). Example: {"mon":{"active":true,"earlyTime":"09:00","lateTime":"17:00"},...}.'
    );
  }

  const action = params.primaryAction;
  if (
    (action === "forward" || action === "forwardvwm") &&
    (!hasOwn(params, "primaryNumber") || isExplicitClear(params.primaryNumber))
  ) {
    errors.push(
      'When setting primaryAction to forward/forwardvwm, also set `primaryNumber` to the E.164 phone (e.g. "+17047050000"). ' +
        "Do not put the phone in `primaryChannel`."
    );
  }

  if (action === "twilanswer_app") {
    if (
      !hasOwn(params, "primaryNumber") ||
      isExplicitClear(params.primaryNumber)
    ) {
      errors.push(
        'When setting primaryAction to twilanswer_app, also set `primaryNumber` to device:<userId> (from appClient_fetchOrgForwardingNumbers mobileDestinations).'
      );
    } else if (!isDeviceSentinel(params.primaryNumber)) {
      errors.push(
        'For twilanswer_app, primaryNumber must be device:<userId>, not an E.164 phone. ' +
          formatMobileDestinationHint()
      );
    } else if (!parseDeviceUserId(params.primaryNumber)) {
      errors.push(
        "primaryNumber is an incomplete device sentinel. Set device:<userId>."
      );
    }
  }

  if (params.offHoursAction === "twilanswer_app") {
    if (
      !hasOwn(params, "offHoursNumber") ||
      isExplicitClear(params.offHoursNumber)
    ) {
      errors.push(
        'When setting offHoursAction to twilanswer_app, also set `offHoursNumber` to device:<userId>.'
      );
    } else if (!isDeviceSentinel(params.offHoursNumber)) {
      errors.push(
        "For twilanswer_app, offHoursNumber must be device:<userId>, not an E.164 phone."
      );
    }
  }

  if (action === "dynamic_forward") {
    const backupIsNone =
      !hasOwn(params, "primaryNumber") ||
      isExplicitClear(params.primaryNumber) ||
      params.primaryNumber === "none" ||
      params.primaryNumber === "";
    const vmExplicitOff =
      params.twilanswerVmFallback === false ||
      params.twilanswerVmFallback === "false";
    // Default twilanswerVmFallback is on; require recording when backup is none or VM is on
    const needsRecording = backupIsNone || !vmExplicitOff;
    if (
      needsRecording &&
      (!hasOwn(params, "primaryRecording") ||
        isExplicitClear(params.primaryRecording))
    ) {
      errors.push(
        'When primaryAction is "dynamic_forward", set primaryRecording when backup is "none" (or omitted) or Twilanswer Voicemail Fallback is enabled. ' +
          'Use primaryNumber: "none" for no backup destination, or an org forwarding number. Set twilanswerVmFallback true|false.'
      );
    }
  }

  if (
    (params.primaryAction === "send_to_vm" ||
      params.primaryAction === "play_recording" ||
      params.primaryAction === "forwardvwm") &&
    (!hasOwn(params, "primaryRecording") ||
      isExplicitClear(params.primaryRecording))
  ) {
    errors.push(
      `When setting primaryAction to ${params.primaryAction}, also set primaryRecording to the recording id (rec_…). ` +
        "Do not use callGreetingRecording for this — that field is only for an optional pre-action greeting."
    );
  }

  if (
    params.primaryAction === "twilanswer_app" &&
    (!hasOwn(params, "primaryRecording") ||
      isExplicitClear(params.primaryRecording) ||
      !params.primaryRecording)
  ) {
    errors.push(
      "When setting primaryAction to twilanswer_app, also set primaryRecording to the voicemail recording id (rec_…). A voicemail recording is required."
    );
  }

  if (
    params.offHoursAction === "twilanswer_app" &&
    (!hasOwn(params, "offHoursRecording") ||
      isExplicitClear(params.offHoursRecording) ||
      !params.offHoursRecording)
  ) {
    errors.push(
      "When setting offHoursAction to twilanswer_app, also set offHoursRecording to the voicemail recording id (rec_…). A voicemail recording is required."
    );
  }

  if (
    (params.offHoursAction === "send_to_vm" ||
      params.offHoursAction === "play_recording" ||
      params.offHoursAction === "forwardvwm") &&
    (!hasOwn(params, "offHoursRecording") ||
      isExplicitClear(params.offHoursRecording))
  ) {
    errors.push(
      `When setting offHoursAction to ${params.offHoursAction}, also set offHoursRecording to a recording id (rec_…).`
    );
  }

  if (params.offHoursAction === "dynamic_forward") {
    const backupIsNone =
      !hasOwn(params, "offHoursNumber") ||
      isExplicitClear(params.offHoursNumber) ||
      params.offHoursNumber === "none" ||
      params.offHoursNumber === "";
    const vmExplicitOff =
      params.offHoursTwilanswerVmFallback === false ||
      params.offHoursTwilanswerVmFallback === "false";
    const needsRecording = backupIsNone || !vmExplicitOff;
    if (
      needsRecording &&
      (!hasOwn(params, "offHoursRecording") ||
        isExplicitClear(params.offHoursRecording))
    ) {
      errors.push(
        'When offHoursAction is "dynamic_forward", set offHoursRecording when backup is "none" or offHoursTwilanswerVmFallback is enabled.'
      );
    }
  }

  const CALLER_ID_PREFS = new Set(["caller", "twilio"]);
  if (
    hasOwn(params, "callerIdPref") &&
    !isExplicitClear(params.callerIdPref) &&
    !CALLER_ID_PREFS.has(String(params.callerIdPref))
  ) {
    errors.push(
      `Invalid callerIdPref "${params.callerIdPref}". Use "caller" (show original caller ID) or "twilio" (show system number).`
    );
  }

  const CALL_APPROVALS = new Set(["enabled", "disabled"]);
  if (
    hasOwn(params, "callApproval") &&
    !isExplicitClear(params.callApproval) &&
    !CALL_APPROVALS.has(String(params.callApproval))
  ) {
    errors.push(
      `Invalid callApproval "${params.callApproval}". Use "enabled" (press 1 to connect) or "disabled" (connect immediately).`
    );
  }

  if (
    hasOwn(params, "primaryNotification") &&
    !isExplicitClear(params.primaryNotification) &&
    (!hasOwn(params, "primaryChannel") || isExplicitClear(params.primaryChannel)) &&
    !patch.primaryChannel
  ) {
    errors.push(
      "You set `primaryNotification` without `primaryChannel`. Set both (channel id from appClient_fetchOrgChannels)."
    );
  }

  if (
    hasOwn(params, "offHoursNotification") &&
    !isExplicitClear(params.offHoursNotification) &&
    (!hasOwn(params, "offHoursChannel") ||
      isExplicitClear(params.offHoursChannel)) &&
    !patch.offHoursChannel
  ) {
    errors.push(
      "You set `offHoursNotification` without `offHoursChannel`. Set both (or reuse the same channel id as primary if appropriate)."
    );
  }

  if (params.primaryAction === "ivr" || patch.primaryAction === "ivr") {
    const menu = patch.ivrMenu;
    if (!menu || typeof menu !== "object") {
      errors.push(
        'When primaryAction is "ivr", also set ivrMenu with menu_recording, hangup_recording, and options. ' +
          "Do not put IVR fields in extra_fields under invented names like ivrMenuRecording."
      );
    } else {
      if (!menu.menu_recording || menu.menu_recording === "disabled") {
        errors.push(
          "ivrMenu.menu_recording is required (recording id rec_… from fetchOrgRecordings)."
        );
      }
      if (!menu.hangup_recording || menu.hangup_recording === "disabled") {
        errors.push(
          "ivrMenu.hangup_recording is required (no-response hangup recording id)."
        );
      }
      const opts = menu.options;
      const entries = opts
        ? Array.isArray(opts)
          ? opts
          : Object.values(opts)
        : [];
      if (!entries.length) {
        errors.push(
          "IVR options were missing or empty after parsing. Pass options as an ARRAY " +
            '(preferred): ivrMenu.options: [{ digit:"1", primaryAction:"forward", primaryNumber:"+17047050000" }, ...]. ' +
            "Digit-keyed objects are often stripped by the tool schema."
        );
      } else {
        entries.forEach((o, i) => {
          const digit = o?.digit ?? i + 1;
          if (!o?.primaryAction) {
            errors.push(`ivrMenu option ${digit}: primaryAction is required.`);
          } else if (
            (o.primaryAction === "forward" || o.primaryAction === "forwardvwm") &&
            !o.primaryNumber
          ) {
            errors.push(
              `ivrMenu option ${digit}: primaryNumber is required for forward.`
            );
          } else if (o.primaryAction === "twilanswer_app") {
            if (!o.primaryNumber || !isDeviceSentinel(o.primaryNumber) || !parseDeviceUserId(o.primaryNumber)) {
              errors.push(
                `ivrMenu option ${digit}: primaryNumber must be device:<userId> for twilanswer_app.`
              );
            }
            if (!o.primaryRecording) {
              errors.push(
                `ivrMenu option ${digit}: primaryRecording is required for twilanswer_app.`
              );
            }
          } else if (o.primaryAction === "dynamic_forward") {
            const backupIsNone = !o.primaryNumber || o.primaryNumber === "none";
            const vmOff = o.twilanswerVmFallback === false;
            if ((backupIsNone || !vmOff) && !o.primaryRecording) {
              errors.push(
                `ivrMenu option ${digit}: primaryRecording is required for dynamic_forward when backup is none or Twilanswer Voicemail Fallback is on.`
              );
            }
          } else if (
            (o.primaryAction === "send_to_vm" ||
              o.primaryAction === "play_recording") &&
            !o.primaryRecording
          ) {
            errors.push(
              `ivrMenu option ${digit}: primaryRecording is required for ${o.primaryAction}.`
            );
          } else if (
            o.primaryAction &&
            !IVR_OPTION_ACTIONS.has(o.primaryAction)
          ) {
            errors.push(
              `ivrMenu option ${digit}: invalid primaryAction "${o.primaryAction}".`
            );
          }
        });
      }
    }
  }

  // Phone stuffed into primaryChannel without a notification type
  if (
    params.primaryChannel &&
    !isExplicitClear(params.primaryChannel) &&
    !params.primaryNotification &&
    /^[\d+\-\s()]+$/.test(String(params.primaryChannel))
  ) {
    errors.push(
      "`primaryChannel` looks like a phone number. Use `primaryNumber` for forwarding. " +
        "`primaryChannel` is only for notification channel ids from appClient_fetchOrgChannels."
    );
  }

  if (
    params.primaryChannel &&
    !isExplicitClear(params.primaryChannel) &&
    !hasOwn(params, "primaryNotification") &&
    !patch.primaryNotification
  ) {
    errors.push(
      "You set `primaryChannel` without `primaryNotification`. " +
        'Set both, e.g. primaryNotification: "front", primaryChannel: "<ch_id>".'
    );
  }

  if (
    params.offHoursChannel &&
    !isExplicitClear(params.offHoursChannel) &&
    !hasOwn(params, "offHoursNotification") &&
    !patch.offHoursNotification
  ) {
    errors.push(
      "You set `offHoursChannel` without `offHoursNotification`. Set both."
    );
  }

  if (
    hasOwn(params, "primaryNotification") &&
    !isExplicitClear(params.primaryNotification) &&
    !normalizeNotificationType(params.primaryNotification)
  ) {
    errors.push(
      `Invalid primaryNotification "${params.primaryNotification}". Use email|slack|sms|front.`
    );
  }

  if (
    hasOwn(params, "offHoursNotification") &&
    !isExplicitClear(params.offHoursNotification) &&
    !normalizeNotificationType(params.offHoursNotification)
  ) {
    errors.push(
      `Invalid offHoursNotification "${params.offHoursNotification}". Use email|slack|sms|front.`
    );
  }

  return errors;
}

function summarizeRecording(rec) {
  const m = rec?.M || rec || {};
  const id = dynAttr(m.rec_id) || dynAttr(m.id) || dynAttr(m.recording_id);
  const isDefault =
    dynAttr(m.default) === true ||
    dynAttr(m.is_default) === true ||
    dynAttr(m.Default) === true;
  return {
    id,
    name: dynAttr(m.title) || dynAttr(m.name) || dynAttr(m.label) || id,
    isDefault: Boolean(isDefault),
  };
}

function summarizeCall(call) {
  return {
    callId: dynAttr(call.callId) || call.callId,
    startTime: dynAttr(call.startTime) || call.startTime,
    fromNumber: dynAttr(call.fromNumber) || call.fromNumber,
    toNumber: dynAttr(call.toNumber) || call.toNumber,
    action: dynAttr(call.action) || call.action,
    actionDetails: dynAttr(call.action_details) || call.action_details,
    hasVm: dynAttr(call.has_vm) ?? call.has_vm ?? false,
    direction: dynAttr(call.direction) || call.direction || null,
  };
}

function summarizeIncomingPhone(n) {
  const phoneNumber = n?.phoneNumber || n?.phone_number || n?.number || null;
  return {
    phoneNumber,
    friendlyName: n?.friendlyName || n?.friendly_name || null,
    sid: n?.sid || n?.phoneNumberSid || n?.phone_number_sid || null,
  };
}

function phonesMatch(a, b) {
  const da = digitsOnly(a);
  const db = digitsOnly(b);
  if (!da || !db) return false;
  return (
    da === db ||
    da === db.slice(-10) ||
    db === da.slice(-10) ||
    (da.length === 10 && db === `1${da}`) ||
    (db.length === 10 && da === `1${db}`)
  );
}

async function loadIncomingPhoneNumbers(backendUrl) {
  const orgId = getOrgId();
  if (!orgId) return [];
  const res = await axios.post(`${backendUrl}twilio/get-incoming-phone-numbers`, {
    organization_id: orgId,
  });
  const raw = Array.isArray(res.data)
    ? res.data
    : res.data?.phoneNumbers || res.data?.incoming_phone_numbers || [];
  return (raw || []).map(summarizeIncomingPhone).filter((n) => n.phoneNumber);
}

/** Cap list sent to the agent; always keep org primary when present. */
const INCOMING_PHONE_RETURN_LIMIT = 20;

function limitIncomingPhoneNumbersForAgent(numbers = [], primary = null) {
  const totalCount = numbers.length;
  if (totalCount <= INCOMING_PHONE_RETURN_LIMIT) {
    return { numbers, totalCount, truncated: false };
  }
  const capped = [];
  const seen = new Set();
  const pushUnique = (n) => {
    if (!n?.phoneNumber) return;
    const key = digitsOnly(n.phoneNumber);
    if (!key || seen.has(key)) return;
    seen.add(key);
    capped.push(n);
  };
  if (primary) {
    const match = numbers.find((n) => phonesMatch(n.phoneNumber, primary));
    if (match) pushUnique(match);
  }
  for (const n of numbers) {
    if (capped.length >= INCOMING_PHONE_RETURN_LIMIT) break;
    pushUnique(n);
  }
  return {
    numbers: capped,
    totalCount,
    truncated: true,
  };
}

/**
 * Pick a default incoming number only when there is exactly one.
 * Do not default to org primary when multiple numbers exist — that risks
 * accidentally binding a new route to the primary.
 */
function pickDefaultIncomingPhone(numbers = []) {
  if (numbers.length === 1) {
    const primary = dynAttr(
      getStoredOrganization()?.twilio_integration?.M?.primary_phone
    );
    return {
      phoneNumber: numbers[0].phoneNumber,
      reason: "only_number",
      isPrimary: phonesMatch(numbers[0].phoneNumber, primary),
    };
  }
  return null;
}

/**
 * Build ElevenLabs clientTools bound to live app deps via getDeps().
 * getDeps() should return: { navigate, bridge, locationPathname }
 */
export function createClientTools(getDeps) {
  const backend = () => process.env.REACT_APP_BACKEND_URL;

  return {
    async appClient_fetchOrgInfo() {
      try {
        const orgId = getOrgId();
        if (!orgId) return JSON.stringify({ error: "No organization in session" });

        const orgRes = await axios.post(`${backend()}get-organization`, {
          orgId,
        });
        const org = orgRes.data?.organization || getStoredOrganization();
        if (org) {
          localStorage.setItem("organization", JSON.stringify(org));
        }

        let routeCount = 0;
        try {
          const routesRes = await axios.post(`${backend()}route/get-routes`, {
            org_id: orgId,
          });
          routeCount = Array.isArray(routesRes.data?.routes)
            ? routesRes.data.routes.length
            : 0;
        } catch {
          routeCount = -1;
        }

        const twilio = org?.twilio_integration?.M || {};
        const hasAccountSid = Boolean(dynAttr(twilio.account_sid) || dynAttr(twilio.AccountSid));
        const hasAuthToken = Boolean(
          dynAttr(twilio.auth_token) || dynAttr(twilio.AuthToken)
        );
        const primaryPhone = dynAttr(twilio.primary_phone);
        const onTwilioTrial =
          dynAttr(twilio.on_trial) === true ||
          dynAttr(twilio.trial) === true ||
          org?.on_twilio_trial?.BOOL === true;
        const orgTimezone =
          dynAttr(org?.org_timezone) ||
          dynAttr(org?.organization_timezone) ||
          null;

        const channels = org?.channels?.L || [];
        const sub = org?.subscription_details?.M || {};
        const subStatus = dynAttr(sub.sub_status);
        const plan = dynAttr(sub.plan);

        return JSON.stringify({
          orgId,
          orgName: dynAttr(org?.organization_name),
          createdDate:
            dynAttr(org?.created) ||
            dynAttr(org?.created_at) ||
            dynAttr(org?.createdDate) ||
            null,
          orgTimezone,
          timezoneNote:
            "Organization timezone for business hours display. Do not ask the user for timezone — you cannot set it via tools. If they ask, tell them the org timezone from this field (or that it is unset in Settings).",
          twilioConfigured: Boolean(hasAccountSid && (hasAuthToken || primaryPhone)),
          twilioPrimaryPhone: primaryPhone || null,
          twilioTrial: Boolean(onTwilioTrial),
          hasRoutes: routeCount > 0,
          routeCount: routeCount < 0 ? null : routeCount,
          hasChannels: channels.length > 0,
          channelCount: channels.length,
          subscriptionStatus: subStatus,
          plan,
          isPaid:
            subStatus === "active" ||
            subStatus === "trialing" ||
            Boolean(dynAttr(sub.stripe_cust_id)),
        });
      } catch (e) {
        return JSON.stringify({
          error: e?.response?.data?.message || e.message || "Failed to fetch org info",
        });
      }
    },

    async appClient_fetchOrgRoutes() {
      try {
        const orgId = getOrgId();
        if (!orgId) return JSON.stringify({ error: "No organization in session" });
        const routesRes = await axios.post(`${backend()}route/get-routes`, {
          org_id: orgId,
        });
        const rawRoutes = Array.isArray(routesRes.data?.routes)
          ? routesRes.data.routes
          : [];
        const routes = rawRoutes.map(summarizeRouteListItem);
        return JSON.stringify({
          routeCount: routes.length,
          routes,
          tip:
            "To edit a route: appClient_navigateTo page=editPath (e.g. /dashboard/call-route/{routeId}), " +
            "then appClient_fetchRouteConfig (no route_id) to read the open editor, then appClient_updateCurrentRouteConfig for partial changes.",
        });
      } catch (e) {
        return JSON.stringify({
          error:
            e?.response?.data?.message || e.message || "Failed to fetch routes",
        });
      }
    },

    async appClient_fetchRouteConfig(params = {}) {
      try {
        const { bridge, locationPathname } = getDeps();
        const requestedId =
          typeof params.route_id === "string" && params.route_id.trim()
            ? params.route_id.trim()
            : null;
        const pathId = routeIdFromPath(locationPathname);
        const onEditor =
          typeof locationPathname === "string" &&
          (locationPathname.includes("/create-route") ||
            locationPathname.includes("/call-route/"));

        // Prefer live editor snapshot when on the route editor and no other id requested
        // (or the requested id matches the open edit page).
        if (
          onEditor &&
          bridge?.getRouteDraft &&
          (!requestedId || (pathId && requestedId === pathId))
        ) {
          const draft = bridge.getRouteDraft();
          if (draft && typeof draft === "object") {
            return JSON.stringify({
              ok: true,
              route: summarizeRouteConfig(
                {
                  ...draft,
                  routeId: draft.routeId || pathId || null,
                },
                { source: "editor" }
              ),
              note:
                "This is the live route editor state (includes unsaved drafts). " +
                "Use appClient_updateCurrentRouteConfig for partial patches; ask the user to click Save.",
            });
          }
        }

        const routeId = requestedId || pathId;
        if (!routeId) {
          return JSON.stringify({
            error:
              "No route_id provided and no route editor is open. " +
              "Call appClient_fetchOrgRoutes, pick a route, navigate to its editPath, or pass route_id.",
          });
        }

        const orgId = getOrgId();
        if (!orgId) return JSON.stringify({ error: "No organization in session" });

        const routeRes = await axios.post(`${backend()}route/get-route`, {
          route_id: routeId,
          org_id: orgId,
        });
        const raw = routeRes.data?.route;
        if (!raw) {
          return JSON.stringify({
            error: `No route found for route_id "${routeId}".`,
          });
        }
        const parsed = parseDynamoItem(raw);
        return JSON.stringify({
          ok: true,
          route: summarizeRouteConfig(parsed, { source: "api" }),
          note:
            "Saved route from the API (not live editor drafts). " +
            "To change it: navigate to route.editPath, then appClient_updateCurrentRouteConfig.",
        });
      } catch (e) {
        return JSON.stringify({
          error:
            e?.response?.data?.message ||
            e.message ||
            "Failed to fetch route config",
        });
      }
    },

    async appClient_fetchOrgRecordings() {
      try {
        const org = getStoredOrganization();
        const recordings = (org?.recordings?.L || []).map(summarizeRecording);
        return JSON.stringify({ recordings });
      } catch (e) {
        return JSON.stringify({ error: e.message || "Failed to fetch recordings" });
      }
    },

    async appClient_fetchOrgForwardingNumbers() {
      try {
        const org = getStoredOrganization();
        const forwarding = (org?.channels?.L || [])
          .map(summarizeChannel)
          .filter((c) => c.type === "forward_number");
        const mobileDestinations = listOrgMobileDestinations();
        return JSON.stringify({
          forwardingNumbers: forwarding,
          mobileDestinations,
          message:
            "forwardingNumbers are E.164 phones for forward/forwardvwm/dynamic_forward backup. " +
            "mobileDestinations are device:<userId> values for primaryAction twilanswer_app " +
            "(prefer hasForwardingDevice true).",
        });
      } catch (e) {
        return JSON.stringify({
          error: e.message || "Failed to fetch forwarding numbers",
        });
      }
    },

    async appClient_fetchIncomingPhoneNumbers() {
      try {
        const orgId = getOrgId();
        if (!orgId) {
          return JSON.stringify({ error: "No organization in session" });
        }
        const numbers = await loadIncomingPhoneNumbers(backend());
        const primary = dynAttr(
          getStoredOrganization()?.twilio_integration?.M?.primary_phone
        );
        const { numbers: limited, totalCount, truncated } =
          limitIncomingPhoneNumbersForAgent(numbers, primary);
        const incomingPhoneNumbers = limited.map((n) => ({
          ...n,
          isPrimary: Boolean(primary && phonesMatch(n.phoneNumber, primary)),
        }));
        const suggested = pickDefaultIncomingPhone(numbers);
        return JSON.stringify({
          incomingPhoneNumbers,
          totalCount,
          truncated,
          returnedCount: incomingPhoneNumbers.length,
          primaryPhone: primary || null,
          suggestedConfiguredPhone: suggested?.phoneNumber || null,
          suggestedReason: suggested?.reason || null,
          message: !numbers.length
            ? "No incoming Twilio numbers found. The user must add a number in Twilio before creating a route."
            : truncated
              ? `Showing ${incomingPhoneNumbers.length} of ${totalCount} Twilio numbers (capped). Ask the user which number this route should answer (they can type/paste it if it is not in this list). Prefer suggestedConfiguredPhone only when it is set (sole number).`
              : numbers.length === 1
                ? "Only one Twilio incoming number — use it (or suggestedConfiguredPhone) for configured_phone."
                : "Multiple Twilio numbers — ask the user which one this route should answer, then set configured_phone. Do not assume the org primary.",
        });
      } catch (e) {
        return JSON.stringify({
          error:
            e?.response?.data?.message ||
            e.message ||
            "Failed to fetch incoming Twilio phone numbers",
        });
      }
    },

    async appClient_fetchOrgChannels() {
      try {
        const org = getStoredOrganization();
        const channels = (org?.channels?.L || []).map(summarizeChannel);
        return JSON.stringify({ channels });
      } catch (e) {
        return JSON.stringify({ error: e.message || "Failed to fetch channels" });
      }
    },

    async appClient_fetchCallsInfo(params = {}) {
      try {
        const orgId = getOrgId();
        if (!orgId) return JSON.stringify({ error: "No organization in session" });
        const days = Number(params.days) > 0 ? Number(params.days) : 3;
        const endTime = Math.floor(Date.now() / 1000);
        const startTime = endTime - days * 24 * 60 * 60;
        const response = await axios.post(`${backend()}call/get-calls`, {
          organization_id: orgId,
          start_time: startTime,
          end_time: endTime,
        });
        const calls = (response.data?.calls || []).map(summarizeCall);
        return JSON.stringify({ days, count: calls.length, calls: calls.slice(0, 25) });
      } catch (e) {
        return JSON.stringify({
          error: e?.response?.data?.message || e.message || "Failed to fetch calls",
        });
      }
    },

    async appClient_promptAddForwardingNumber(params = {}) {
      try {
        const { bridge } = getDeps();
        if (!bridge?.openAddChannelModal) {
          return JSON.stringify({ error: "UI bridge not available" });
        }
        bridge.openAddChannelModal("forward_number", {
          initialValue: params.phone_number || "",
        });
        return JSON.stringify({
          ok: true,
          message:
            "Opened add forwarding number modal. Wait for a contextual update when the user finishes.",
        });
      } catch (e) {
        return JSON.stringify({ error: e.message });
      }
    },

    async appClient_promptAddRecording() {
      try {
        const { bridge } = getDeps();
        if (!bridge?.openAddRecordingModal) {
          return JSON.stringify({ error: "UI bridge not available" });
        }
        bridge.openAddRecordingModal();
        return JSON.stringify({
          ok: true,
          message:
            "Opened add recording modal. Wait for a contextual update when the user finishes.",
        });
      } catch (e) {
        return JSON.stringify({ error: e.message });
      }
    },

    async appClient_promptAddChannel(params = {}) {
      try {
        const { bridge } = getDeps();
        const channelType = params.channel_type;
        if (!channelType) {
          return JSON.stringify({ error: "channel_type is required" });
        }
        if (!bridge?.openAddChannelModal) {
          return JSON.stringify({ error: "UI bridge not available" });
        }
        bridge.openAddChannelModal(channelType);
        return JSON.stringify({
          ok: true,
          message: `Opened add ${channelType} channel modal. Wait for a contextual update when the user finishes.`,
        });
      } catch (e) {
        return JSON.stringify({ error: e.message });
      }
    },

    async appClient_promptContactSupport(params = {}) {
      try {
        const { bridge } = getDeps();
        if (!bridge?.openSupportComposeModal) {
          return JSON.stringify({ error: "UI bridge not available" });
        }
        bridge.openSupportComposeModal({
          subject: params.subject || "",
          body: params.body || "",
        });
        return JSON.stringify({
          ok: true,
          message:
            "Opened contact support compose window. Wait for a support_sent contextual update when the user sends, or continue if they dismiss it.",
        });
      } catch (e) {
        return JSON.stringify({ error: e.message });
      }
    },

    async appClient_updateCurrentRouteConfig(params = {}) {
      try {
        const { bridge, locationPathname } = getDeps();
        const onRouteEditor =
          typeof locationPathname === "string" &&
          (locationPathname.includes("/create-route") ||
            locationPathname.includes("/call-route/"));

        if (!onRouteEditor) {
          return JSON.stringify({
            error:
              "User is not on the route editor. Call appClient_navigateTo with create-route or an existing call-route first, then retry.",
          });
        }
        if (!bridge?.draftRouteUpdate) {
          return JSON.stringify({
            error: "Route editor is not ready to accept drafts yet.",
          });
        }

        const {
          business_hours,
          business_hours_json,
          extra_fields,
          clear_fields,
          notification, // rejected by validateRouteDraftParams
          ivrMenuRecording,
          ivrHangupRecording,
          ivrNoResponseRecording,
          ivrOptions,
          menu_recording,
          hangup_recording,
          ...rest
        } = params || {};

        // Strip inventedish IVR aliases from the flat patch; they are folded into ivrMenu below.
        const patch = { ...rest };
        delete patch.ivrMenuRecording;
        delete patch.ivrHangupRecording;
        delete patch.ivrNoResponseRecording;
        delete patch.ivrOptions;
        delete patch.menu_recording;
        delete patch.hangup_recording;
        delete patch.business_hours_json;
        delete patch.calendar;

        // Notification fields must come from original params so null clears are detectable
        delete patch.primaryNotification;
        delete patch.primaryChannel;
        delete patch.offHoursNotification;
        delete patch.offHoursChannel;

        // Prefer structured business_hours; fall back to JSON string or calendar alias.
        // Nested objects without a schema often arrive as {} from the client-tool runtime.
        const calendar =
          coerceBusinessHoursCalendar(business_hours) ||
          coerceBusinessHoursCalendar(business_hours_json) ||
          coerceBusinessHoursCalendar(params?.calendar);
        if (calendar) {
          patch.calendar = calendar;
        }

        Object.assign(patch, buildNotificationPatch(params || {}));
        applyClearFields(patch, clear_fields);

        const extra =
          extra_fields && typeof extra_fields === "object" ? { ...extra_fields } : {};
        // Pull known IVR aliases out of extra_fields so they don't land as junk root keys
        const {
          ivrMenuRecording: efMenuRec,
          ivrHangupRecording: efHangup,
          ivrNoResponseRecording: efNoResp,
          ivrOptions: efOptions,
          menu_recording: efMenuRec2,
          hangup_recording: efHangup2,
          ivrMenu: efIvrMenu,
          ...extraRest
        } = extra;
        void efMenuRec;
        void efHangup;
        void efNoResp;
        void efOptions;
        void efMenuRec2;
        void efHangup2;
        void efIvrMenu;

        const ivrMenu = buildIvrMenuFromParams(
          {
            ...params,
            ivrMenu: params.ivrMenu ?? efIvrMenu,
            ivrMenuRecording: ivrMenuRecording ?? efMenuRec,
            ivrHangupRecording: ivrHangupRecording ?? efHangup,
            ivrNoResponseRecording: ivrNoResponseRecording ?? efNoResp,
            ivrOptions: ivrOptions ?? efOptions,
            menu_recording: menu_recording ?? efMenuRec2,
            hangup_recording: hangup_recording ?? efHangup2,
          },
          {}
        );
        if (ivrMenu) {
          patch.ivrMenu = ivrMenu;
          if (!patch.primaryAction) patch.primaryAction = "ivr";
        }

        // twilanswer_app: twilanswerVmFallback follows whether a recording is set
        if (patch.primaryAction === TWILANSWER_APP_ACTION) {
          const hasRec =
            hasOwn(params, "primaryRecording") &&
            !isExplicitClear(params.primaryRecording) &&
            Boolean(params.primaryRecording);
          if (hasOwn(params, "primaryRecording") || !hasOwn(params, "twilanswerVmFallback")) {
            patch.twilanswerVmFallback = hasRec;
          }
        }
        if (patch.offHoursAction === TWILANSWER_APP_ACTION) {
          const hasRec =
            hasOwn(params, "offHoursRecording") &&
            !isExplicitClear(params.offHoursRecording) &&
            Boolean(params.offHoursRecording);
          if (
            hasOwn(params, "offHoursRecording") ||
            !hasOwn(params, "offHoursTwilanswerVmFallback")
          ) {
            patch.offHoursTwilanswerVmFallback = hasRec;
          }
        }

        // The Lambda treats unset fields as caller ID "caller", approval "disabled",
        // VM off — fill Dynamic Forward's defaults for fields the route hasn't set.
        const currentDraft =
          typeof bridge.getRouteDraft === "function" ? bridge.getRouteDraft() || {} : {};
        const fillDynamicDefault = (key, value) => {
          if (hasOwn(patch, key)) return;
          const existing = currentDraft[key];
          if (existing != null && existing !== "") return;
          patch[key] = value;
        };
        if (patch.primaryAction === "dynamic_forward") {
          fillDynamicDefault("twilanswerVmFallback", true);
          fillDynamicDefault("callerIdPref", "twilio");
          fillDynamicDefault("callApproval", "enabled");
        }
        if (patch.offHoursAction === "dynamic_forward") {
          fillDynamicDefault("offHoursTwilanswerVmFallback", true);
          fillDynamicDefault("offHoursCallerIdPref", "twilio");
          fillDynamicDefault("callApproval", "enabled");
        }

        // New routes need an Incoming Call Twilio number (configured_phone).
        // On create-route, auto-fill from org primary / sole number when omitted.
        let configuredPhoneAutoFilled = false;
        const isCreateRoute =
          typeof locationPathname === "string" &&
          locationPathname.includes("/create-route");
        const needsConfiguredPhone = Boolean(
          patch.primaryAction || patch.ivrMenu || isCreateRoute
        );
        if (
          needsConfiguredPhone &&
          isCreateRoute &&
          (patch.configured_phone == null || patch.configured_phone === "")
        ) {
          try {
            const numbers = await loadIncomingPhoneNumbers(backend());
            const suggested = pickDefaultIncomingPhone(numbers);
            if (suggested?.phoneNumber) {
              patch.configured_phone = suggested.phoneNumber;
              configuredPhoneAutoFilled = true;
            } else if (!numbers.length) {
              return JSON.stringify({
                error:
                  "This route needs configured_phone (Incoming Call Twilio number), but the org has no incoming Twilio numbers. Ask the user to add a number in Twilio first.",
                hint: {
                  nextStep: "Add an incoming number in Twilio, then retry.",
                },
              });
            } else {
              const { numbers: limited, totalCount, truncated } =
                limitIncomingPhoneNumbersForAgent(numbers);
              return JSON.stringify({
                error:
                  "This route needs configured_phone. Multiple Twilio incoming numbers exist — call appClient_fetchIncomingPhoneNumbers, pick one, and pass configured_phone.",
                hint: {
                  incomingPhoneNumbers: limited,
                  totalCount,
                  truncated,
                  example: {
                    configured_phone: limited[0]?.phoneNumber,
                    primaryAction: "forward",
                    primaryNumber: "+17047050000",
                  },
                },
              });
            }
          } catch (e) {
            return JSON.stringify({
              error:
                "Could not resolve configured_phone automatically. Call appClient_fetchIncomingPhoneNumbers and set configured_phone explicitly. " +
                (e?.response?.data?.message || e.message || ""),
            });
          }
        }

        const validationErrors = validateRouteDraftParams(params || {}, patch);
        if (validationErrors.length) {
          return JSON.stringify({
            error: validationErrors.join(" "),
            hint: {
              forwardExample: {
                configured_phone: "+17045551212",
                primaryAction: "forward",
                primaryNumber: "+17047050000",
              },
              forwardWithVmExample: {
                configured_phone: "+17045551212",
                primaryAction: "forwardvwm",
                primaryNumber: "+17047050000",
                primaryRecording: "rec_…",
                callApproval: "enabled",
              },
              dynamicForwardExample: {
                configured_phone: "+17045551212",
                primaryAction: "dynamic_forward",
                primaryNumber: "none",
                twilanswerVmFallback: true,
                primaryRecording: "rec_…",
                callerIdPref: "twilio",
                callApproval: "enabled",
              },
              twilanswerAppExample: {
                configured_phone: "+17045551212",
                primaryAction: "twilanswer_app",
                primaryNumber: "device:user_abc",
                primaryRecording: "rec_…",
                twilanswerVmFallback: true,
              },
              notificationExample: {
                primaryNotification: "front",
                primaryChannel: "cha_m15ge",
              },
              bothExample: {
                configured_phone: "+17045551212",
                primaryAction: "forward",
                primaryNumber: "+17047050000",
                primaryNotification: "front",
                primaryChannel: "cha_m15ge",
              },
              voicemailExample: {
                configured_phone: "+17045551212",
                primaryAction: "send_to_vm",
                primaryRecording: "rec_…",
                primaryNotification: "email",
                primaryChannel: "<email channel id>",
              },
              ivrExample: {
                configured_phone: "+17045551212",
                primaryAction: "ivr",
                ivrMenu: {
                  menu_recording: "rec_…",
                  hangup_recording: "rec_…",
                  options: [
                    {
                      digit: "1",
                      primaryAction: "forward",
                      primaryNumber: "+17047050000",
                    },
                    {
                      digit: "2",
                      primaryAction: "forward",
                      primaryNumber: "+19843827368",
                    },
                  ],
                },
              },
              clearExample: {
                clear_fields: ["primaryNotification", "primaryChannel"],
              },
              incomingPhoneTip:
                "Call appClient_fetchIncomingPhoneNumbers and set configured_phone to one of the phoneNumber values before drafting.",
            },
          });
        }

        // Reject phones / channels / recordings that are not on this org
        const orgRefErrors = validateOrgEntityRefs(patch);
        if (orgRefErrors.length) {
          return JSON.stringify({
            error: orgRefErrors.join(" "),
            hint: {
              nextSteps: [
                "Call appClient_fetchOrgForwardingNumbers before setting primaryNumber / offHoursNumber.",
                "Call appClient_fetchOrgChannels before setting primaryChannel / offHoursChannel.",
                "Call appClient_fetchOrgRecordings before setting primaryRecording / IVR recordings.",
                "To add a missing forwarding number, use appClient_promptAddForwardingNumber (no phone in chat) and wait for channel_added.",
                "To add a missing recording, use appClient_promptAddRecording and wait for recording_added.",
              ],
              forwardingNumbers: listOrgForwardingNumbers().map((f) => ({
                id: f.id,
                value: f.value,
                label: f.label,
              })),
              channels: listOrgChannels()
                .filter((c) => NOTIFICATION_TYPES.has(c.type))
                .map((c) => ({
                  id: c.id,
                  type: c.type,
                  label: c.label,
                  value: c.value,
                })),
              recordings: listOrgRecordings().map((r) => ({
                id: r.id,
                name: r.name,
              })),
            },
          });
        }

        // configured_phone must be a real Twilio incoming number on this org
        if (patch.configured_phone) {
          try {
            const numbers = await loadIncomingPhoneNumbers(backend());
            const match = numbers.find((n) =>
              phonesMatch(n.phoneNumber, patch.configured_phone)
            );
            if (!match) {
              const { numbers: limited, totalCount, truncated } =
                limitIncomingPhoneNumbersForAgent(numbers);
              return JSON.stringify({
                error:
                  `configured_phone "${patch.configured_phone}" is not an incoming Twilio number on this organization. ` +
                  "Call appClient_fetchIncomingPhoneNumbers and use one of the phoneNumber values.",
                hint: {
                  incomingPhoneNumbers: limited,
                  totalCount,
                  truncated,
                  suggestedConfiguredPhone:
                    pickDefaultIncomingPhone(numbers)?.phoneNumber || null,
                },
              });
            }
            // Canonicalize to Twilio's formatting
            patch.configured_phone = match.phoneNumber;
          } catch (e) {
            return JSON.stringify({
              error:
                "Could not verify configured_phone against Twilio incoming numbers. " +
                (e?.response?.data?.message || e.message || ""),
            });
          }
        }

        // Resolve forwarding refs to canonical org E.164 values (validated above)
        // Leave device:<userId> sentinels unchanged.
        if (
          patch.primaryNumber &&
          patch.primaryNumber !== "none" &&
          !isDeviceSentinel(patch.primaryNumber)
        ) {
          patch.primaryNumber =
            findForwardingNumberValue(patch.primaryNumber) || patch.primaryNumber;
        }
        if (
          patch.offHoursNumber &&
          patch.offHoursNumber !== "none" &&
          !isDeviceSentinel(patch.offHoursNumber)
        ) {
          patch.offHoursNumber =
            findForwardingNumberValue(patch.offHoursNumber) ||
            patch.offHoursNumber;
        }
        if (patch.ivrMenu?.options) {
          const opts = patch.ivrMenu.options;
          const rewrite = (o) => {
            if (!o?.primaryNumber || o.primaryNumber === "none") return o;
            if (isDeviceSentinel(o.primaryNumber)) return o;
            const resolved = findForwardingNumberValue(o.primaryNumber);
            return resolved ? { ...o, primaryNumber: resolved } : o;
          };
          if (Array.isArray(opts)) {
            patch.ivrMenu.options = opts.map(rewrite);
          } else if (typeof opts === "object") {
            patch.ivrMenu.options = Object.fromEntries(
              Object.entries(opts).map(([k, o]) => [k, rewrite(o)])
            );
          }
        }

        // Allow remaining known extra fields (rare), but never reintroduce IVR aliases
        if (Object.keys(extraRest).length) {
          Object.assign(patch, extraRest);
        }

        // Omitted keys stay unchanged. Explicit null clears. Drop only undefined.
        Object.keys(patch).forEach((k) => {
          if (patch[k] === undefined) delete patch[k];
          if (patch[k] === "") patch[k] = null;
        });

        const cleared = Object.keys(patch).filter((k) => patch[k] === null);
        const result = bridge.draftRouteUpdate(patch);
        // Prefer the editor's merged snapshot over the partial patch so the agent
        // can verify prior fields (calendar, actions) were retained.
        const drafted = {
          configured_phone:
            result?.configured_phone ?? patch.configured_phone ?? null,
          businessHoursEnabled:
            result?.businessHoursEnabled ??
            patch.businessHoursEnabled ??
            null,
          calendar: result?.calendar ?? patch.calendar ?? null,
          primaryAction: result?.primaryAction ?? patch.primaryAction ?? null,
          primaryNumber: result?.primaryNumber ?? patch.primaryNumber ?? null,
          primaryRecording:
            result?.primaryRecording ?? patch.primaryRecording ?? null,
          primaryNotification:
            result?.primaryNotification ?? patch.primaryNotification ?? null,
          primaryChannel:
            result?.primaryChannel ?? patch.primaryChannel ?? null,
          callerIdPref: result?.callerIdPref ?? patch.callerIdPref ?? null,
          callApproval: result?.callApproval ?? patch.callApproval ?? null,
          twilanswerVmFallback:
            result?.twilanswerVmFallback ?? patch.twilanswerVmFallback ?? null,
          offHoursTwilanswerVmFallback:
            result?.offHoursTwilanswerVmFallback ??
            patch.offHoursTwilanswerVmFallback ??
            null,
          callGreetingEnabled:
            result?.callGreetingEnabled ?? patch.callGreetingEnabled ?? null,
          callGreetingRecording:
            result?.callGreetingRecording ??
            patch.callGreetingRecording ??
            null,
          offHoursAction:
            result?.offHoursAction ?? patch.offHoursAction ?? null,
          offHoursNumber:
            result?.offHoursNumber ?? patch.offHoursNumber ?? null,
          offHoursNotification:
            result?.offHoursNotification ?? patch.offHoursNotification ?? null,
          offHoursChannel:
            result?.offHoursChannel ?? patch.offHoursChannel ?? null,
          offHoursRecording:
            result?.offHoursRecording ?? patch.offHoursRecording ?? null,
          ivrMenu: summarizeIvrMenu(result?.ivrMenu ?? patch.ivrMenu),
        };
        if (
          patch.businessHoursEnabled === true &&
          !hasActiveCalendarDays(drafted.calendar)
        ) {
          return JSON.stringify({
            error:
              "Draft applied but business hours calendar is still empty. Re-send business_hours with active days, or business_hours_json as a JSON string of that schedule.",
            drafted,
            hint: {
              business_hours_example: {
                mon: { active: true, earlyTime: "09:00", lateTime: "17:00" },
                tue: { active: true, earlyTime: "09:00", lateTime: "17:00" },
                wed: { active: true, earlyTime: "09:00", lateTime: "17:00" },
                thur: { active: true, earlyTime: "09:00", lateTime: "17:00" },
                fri: { active: true, earlyTime: "09:00", lateTime: "17:00" },
                sat: { active: false, earlyTime: "09:00", lateTime: "17:00" },
                sun: { active: false, earlyTime: "09:00", lateTime: "17:00" },
              },
            },
          });
        }
        return JSON.stringify({
          ok: true,
          applied: result?.applied ?? true,
          cleared,
          configuredPhoneAutoFilled,
          drafted,
          message:
            "Draft applied on the route editor. Ask the user to review and click Save. Do not claim it is saved until you receive a route-saved update." +
            " Do NOT call appClient_navigateTo create-route again after drafting — that remounts the editor and wipes the draft." +
            (configuredPhoneAutoFilled
              ? " configured_phone was auto-filled from the org Twilio primary/only number — confirm it looks right."
              : ""),
        });
      } catch (e) {
        return JSON.stringify({ error: e.message });
      }
    },

    async appClient_navigateTo(params = {}) {
      try {
        const { navigate, locationPathname } = getDeps();
        if (!navigate) return JSON.stringify({ error: "navigate unavailable" });
        const path = resolveNavigatePath(params.page, params.highlight_call_id);
        const current =
          typeof locationPathname === "string"
            ? locationPathname.split("?")[0].replace(/\/+$/, "") || "/"
            : "";
        const target = String(path || "").split("?")[0].replace(/\/+$/, "") || "/";
        // Navigating to the same create-route path remounts the editor and wipes drafts.
        if (current === target) {
          return JSON.stringify({
            ok: true,
            path,
            alreadyThere: true,
            message:
              "Already on this page — did not re-navigate (re-navigation would wipe an unsaved route draft).",
          });
        }
        navigate(path);
        return JSON.stringify({ ok: true, path });
      } catch (e) {
        return JSON.stringify({ error: e.message });
      }
    },

    async appClient_inviteUser() {
      try {
        requireAdmin("invite users");
        const { navigate } = getDeps();
        if (!navigate) {
          return JSON.stringify({ error: "navigate unavailable" });
        }
        navigate("/dashboard/settings/users?addUser=1");
        return JSON.stringify({
          ok: true,
          message: "Opened Add User modal on the Users settings page.",
        });
      } catch (e) {
        return JSON.stringify({
          error: e.message,
          permissionDenied: Boolean(e.isPermissionError),
        });
      }
    },

    async appClient_controlDialer(params = {}) {
      try {
        const { navigate } = getDeps();
        if (!navigate) return JSON.stringify({ error: "navigate unavailable" });
        const recipient = params.recipient;
        if (recipient) {
          navigate(
            `/dashboard/dialer?recipient_number=${encodeURIComponent(recipient)}`
          );
        } else {
          navigate("/dashboard/dialer");
        }
        return JSON.stringify({
          ok: true,
          message: recipient
            ? `Opened dialer with recipient prefilled.`
            : "Opened dialer.",
        });
      } catch (e) {
        return JSON.stringify({ error: e.message });
      }
    },
  };
}
